import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Scale } from "lucide-react";

export default function RealisationsTradeoffCard({ prompt, onDone }) {
  const [saving, setSaving] = useState(null);
  const navigate = useNavigate();

  if (!prompt) return null;

  const respond = async (code) => {
    if (saving) return;
    setSaving(code);
    try {
      const { data } = await api.post(
        "/realisations/tradeoffs/" + prompt.prompt_id + "/respond",
        { response: code }
      );

      if (data?.action === "accept") {
        toast.success("Trade-off noted — WUA will remember that this is a conscious choice.");
      } else if (data?.action === "reject") {
        toast.success("Got it — cost matters more here.");
      } else if (data?.action === "budget_changed") {
        toast.success("Update your living budget and WUA can reassess this.");
        onDone?.();
        navigate("/profile#profile-field-annual_living_budget_usd");
        return;
      } else {
        toast.success("No problem — we'll leave that trade-off open.");
      }
      onDone?.();
    } catch {
      toast.error("We couldn't save that just now.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card
      className="mt-6 border-amber-200 bg-amber-50/40 p-5 sm:p-6"
      data-testid="realisations-tradeoff-card"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-full bg-amber-100 p-2 text-amber-800">
          <Scale className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="label-uppercase text-amber-800">
            A trade-off to consider
          </div>
          <h2 className="font-editorial text-xl sm:text-2xl mt-1 text-stone-900" data-testid="realisations-tradeoff-question">
            {prompt.question}
          </h2>
          <p className="text-sm text-stone-600 mt-2" data-testid="realisations-tradeoff-helper">
            {prompt.helper_text}
          </p>

          <div className="mt-4 rounded-md border border-amber-200 bg-white px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-stone-500">
              What this choice means
            </div>
            <div className="text-sm sm:text-base text-stone-900 mt-1">
              {prompt.statement}
            </div>
            {prompt.evidence_lines?.length > 0 ? (
              <ul className="mt-2 space-y-1 text-xs text-stone-600" data-testid="realisations-tradeoff-evidence">
                {prompt.evidence_lines.map((l) => <li key={l}>{l}</li>)}
              </ul>
            ) : prompt.target?.university_name && (
              <div className="mt-1 text-xs text-stone-500">
                Based on {prompt.target.university_name} in {prompt.target.city_name}.
              </div>
            )}
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {(prompt.options || []).map((option) => (
              <button
                key={option.code}
                type="button"
                data-testid={"realisations-tradeoff-" + option.code}
                disabled={Boolean(saving)}
                onClick={() => respond(option.code)}
                className={
                  "rounded-full border px-3 py-2 text-sm transition-colors " +
                  (option.code === "accept"
                    ? "border-[#4C1D95] bg-[#4C1D95] text-white hover:bg-[#3d1778]"
                    : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95]") +
                  (saving ? " opacity-60" : "")
                }
              >
                {saving === option.code ? "Saving…" : option.label}
              </button>
            ))}
          </div>

          <p className="mt-3 text-xs text-stone-500">
            This does not change your recommendation scores. It records what
            you are willing to trade so WUA can understand your decisions better.
          </p>
        </div>
      </div>
    </Card>
  );
}
